import React, { useState, useMemo, useCallback } from 'react';
import { Box, Grid, Paper, Typography, Button, Alert, CircularProgress } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQueryClient } from '@tanstack/react-query';

import PeriodSelector from './filters/PeriodSelector';
import DateRangeFilter from './filters/DateRangeFilter';
import UserFilter from './filters/UserFilter';
import KPIGrid from './kpi/KPIGrid';
import JobsCreatedChart from './charts/JobsCreatedChart';
import ConversionFunnelChart from './charts/ConversionFunnelChart';
import SalesPerformanceChart from './charts/SalesPerformanceChart';
import LeadSourceChart from './charts/LeadSourceChart';
import RevenueByMonthChart from './charts/RevenueByMonthChart';
import RevenueByUserChart from './charts/RevenueByUserChart';
import CycleTimeChart from './charts/CycleTimeChart';
import PresentationOutcomeChart from './charts/PresentationOutcomeChart';
import AppointmentCompletionChart from './charts/AppointmentCompletionChart';
import SpecBreakdownChart from './charts/SpecBreakdownChart';
import UserPerformanceTable from './tables/UserPerformanceTable';
import ReportExportDialog from './pdf/ReportExportDialog';

import {
  useDashboardSummary,
  useJobsCreatedReport,
  useConversionFunnel,
  useUserComparison,
  useLeadSources,
  useRevenueByMonth,
  useRevenueByUser,
  useCycleTime,
  usePresentationOutcomes,
  useAppointmentCompletion,
  useSpecBreakdown,
} from '../../hooks/useReports';
import { useBusinessUsers } from '../../hooks/useBusiness';
import { useAuth } from '../../contexts/AuthContext';
import { PeriodType, ExtendedReportRequest, GroupByType } from '../../types/reporting';
import { queryKeys } from '../../config/queryKeys';

const ReportingDashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: businessUsers, isLoading: usersLoading } = useBusinessUsers({
    businessId: user?.businessId ?? undefined,
    enabled: !!user?.businessId,
  });

  // Filter state
  const [period, setPeriod] = useState<PeriodType>('month');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [selectedUsersForExport, setSelectedUsersForExport] = useState<string[]>([]);

  // Compute group by based on period
  const groupBy: GroupByType = useMemo(() => {
    switch (period) {
      case 'month':
        return 'day';
      case 'quarter':
        return 'week';
      case 'year':
      case 'financial-year':
        return 'month';
      default:
        return 'day';
    }
  }, [period]);

  // Build request params
  const requestParams: ExtendedReportRequest = useMemo(() => {
    const params: ExtendedReportRequest = {
      period: period === 'custom' ? 'month' : period,
      groupBy,
    };

    if (period === 'custom' && fromDate && toDate) {
      params.fromDate = fromDate.toISOString();
      params.toDate = toDate.toISOString();
    }

    if (selectedUserIds.length > 0) {
      params.userIds = selectedUserIds;
    }

    return params;
  }, [period, fromDate, toDate, selectedUserIds, groupBy]);

  // Fetch data
  const dashboardSummary = useDashboardSummary(requestParams);
  const jobsCreated = useJobsCreatedReport(requestParams);
  const conversionFunnel = useConversionFunnel(requestParams);
  const userComparison = useUserComparison(requestParams);
  const leadSources = useLeadSources(requestParams);
  const revenueByMonth = useRevenueByMonth(requestParams);
  const revenueByUser = useRevenueByUser(requestParams);
  const cycleTime = useCycleTime(requestParams);
  const presentationOutcomes = usePresentationOutcomes(requestParams);
  const appointmentCompletion = useAppointmentCompletion(requestParams);
  const specBreakdown = useSpecBreakdown(requestParams);

  // Get users for filter
  const users = useMemo(() => {
    return (
      businessUsers?.map((u: any) => ({
        id: u.id,
        name: `${u.firstName} ${u.lastName}`,
      })) || []
    );
  }, [businessUsers]);

  const isLoading =
    dashboardSummary.isLoading ||
    jobsCreated.isLoading ||
    conversionFunnel.isLoading ||
    userComparison.isLoading ||
    leadSources.isLoading ||
    revenueByMonth.isLoading ||
    revenueByUser.isLoading ||
    cycleTime.isLoading ||
    presentationOutcomes.isLoading ||
    appointmentCompletion.isLoading ||
    specBreakdown.isLoading;

  const hasError =
    dashboardSummary.isError ||
    jobsCreated.isError ||
    conversionFunnel.isError ||
    userComparison.isError ||
    leadSources.isError ||
    revenueByMonth.isError ||
    revenueByUser.isError ||
    cycleTime.isError ||
    presentationOutcomes.isError ||
    appointmentCompletion.isError ||
    specBreakdown.isError;

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
  }, [queryClient]);

  const handleExportPDF = () => {
    setSelectedUsersForExport(userComparison.data?.users?.map((u) => u.userId) || []);
    setExportDialogOpen(true);
  };

  const isCustomPeriod = period === 'custom';
  const isCustomPeriodValid = !isCustomPeriod || (fromDate && toDate);

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h4" component="h1">
          Reporting Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={isLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleExportPDF}
            disabled={isLoading}
          >
            Export PDF
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            alignItems: 'flex-end',
          }}
        >
          <PeriodSelector value={period} onChange={setPeriod} />
          {isCustomPeriod && (
            <DateRangeFilter
              fromDate={fromDate}
              toDate={toDate}
              onFromDateChange={setFromDate}
              onToDateChange={setToDate}
            />
          )}
          <UserFilter
            users={users}
            selectedUserIds={selectedUserIds}
            onChange={setSelectedUserIds}
            loading={usersLoading}
          />
        </Box>
        {isCustomPeriod && !isCustomPeriodValid && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Please select both start and end dates for custom period.
          </Alert>
        )}
      </Paper>

      {/* Error state */}
      {hasError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          An error occurred while loading report data. Please try refreshing.
        </Alert>
      )}

      {/* Period label */}
      {dashboardSummary.data && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Showing data for: {dashboardSummary.data.period}
        </Typography>
      )}

      {/* KPI Cards */}
      <Box sx={{ mb: 3 }}>
        <KPIGrid data={dashboardSummary.data} loading={dashboardSummary.isLoading} />
      </Box>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <JobsCreatedChart
            data={jobsCreated.data?.jobsByDate}
            loading={jobsCreated.isLoading}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <ConversionFunnelChart
            data={conversionFunnel.data?.stages}
            loading={conversionFunnel.isLoading}
          />
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <SalesPerformanceChart
            data={jobsCreated.data?.jobsByUser}
            loading={jobsCreated.isLoading}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <LeadSourceChart data={leadSources.data} loading={leadSources.isLoading} />
        </Grid>
      </Grid>

      {/* Charts Row 3 — Revenue */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={7}>
          <RevenueByMonthChart data={revenueByMonth.data} loading={revenueByMonth.isLoading} />
        </Grid>
        <Grid item xs={12} lg={5}>
          <RevenueByUserChart data={revenueByUser.data} loading={revenueByUser.isLoading} />
        </Grid>
      </Grid>

      {/* Charts Row 4 — Cycle Time + Presentation Outcomes */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={7}>
          <CycleTimeChart data={cycleTime.data} loading={cycleTime.isLoading} />
        </Grid>
        <Grid item xs={12} lg={5}>
          <PresentationOutcomeChart
            data={presentationOutcomes.data}
            loading={presentationOutcomes.isLoading}
          />
        </Grid>
      </Grid>

      {/* Charts Row 5 — Appointment Completion */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <AppointmentCompletionChart
            data={appointmentCompletion.data}
            loading={appointmentCompletion.isLoading}
          />
        </Grid>
      </Grid>

      {/* Charts Row 6 — Kitchen Spec Breakdown */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <SpecBreakdownChart data={specBreakdown.data} loading={specBreakdown.isLoading} />
        </Grid>
      </Grid>

      {/* User Performance Table */}
      <Box sx={{ mb: 3 }}>
        <UserPerformanceTable
          data={userComparison.data?.users}
          totals={userComparison.data?.totals}
          loading={userComparison.isLoading}
          selectable={false}
        />
      </Box>

      {/* Export Dialog */}
      <ReportExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        users={userComparison.data?.users || []}
        selectedUserIds={selectedUsersForExport}
        onSelectionChange={setSelectedUsersForExport}
        dashboardSummary={dashboardSummary.data}
        jobsCreated={jobsCreated.data}
        conversionFunnel={conversionFunnel.data}
        leadSources={leadSources.data}
        userComparison={userComparison.data}
        period={dashboardSummary.data?.period || ''}
      />
    </Box>
  );
};

export default ReportingDashboard;
